import type { AgentBuilderKpiCard, AgentBuilderReportData, DashboardValueFormat, DashboardWidget, WidgetHydrationState } from './types'
import {
  deriveChartData,
  deriveKpiCards,
  formatColumnValue,
  formatFieldDisplayName,
  formatInsightValue,
  resolveColumnFormat,
  resolveInsightValueFormat,
  type InsightValueFormat,
} from './vizUtils'
import { isNumericFormatKind, type FieldFormat } from '../../lib/fieldFormat'
import { findByLabel, stripTableQualifier } from './labelMatching'

function getColumnsFromRows(rows: Record<string, unknown>[], widget: DashboardWidget, widgetState: WidgetHydrationState) {
  if (widgetState.columns?.length) {
    return widgetState.columns
  }

  const keys = new Set<string>()
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => keys.add(key))
  })

  if (keys.size > 0) {
    return Array.from(keys)
  }

  return [
    ...(widget.dimensions ?? []).map((dimension) => dimension?.name).filter((name): name is string => Boolean(name)),
    ...(widget.measures ?? []).map((measure) => measure?.name).filter((name): name is string => Boolean(name)),
  ]
}

function normalizeCellValue(value: unknown): string | number | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return value
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return JSON.stringify(value)
}

function getActiveDimensions(widget: DashboardWidget, widgetState: WidgetHydrationState) {
  const daxDimensions = widget.daxGenerationRequest?.dimensions ?? []
  const preferred = widgetState.groupByDimension ?? widget.rowDimension ?? daxDimensions[0] ?? null
  const dimensions = [
    preferred,
    ...daxDimensions,
    ...(widget.dimensions ?? []).map((dimension) => dimension?.name),
  ].filter((value): value is string => Boolean(value))

  return Array.from(new Set(dimensions))
}

function resolveKpiCards(widget: DashboardWidget, widgetState: WidgetHydrationState, columns: string[], rows: Record<string, string | number | null>[]) {
  const backendCards = widgetState.kpiCards ?? []
  const derivedCards = deriveKpiCards(columns, rows)

  if (widget.type === 'kpi' && derivedCards.length > backendCards.length) {
    return derivedCards
  }

  return backendCards.length ? backendCards : derivedCards
}

/** Matches a returned column or card key to a configured measure, with or without its "'Table'[...]" qualifier. */
function findByMeasure<T>(items: T[], field: string, labelsOf: (item: T) => string[]): T | null {
  return findByLabel(items, field, labelsOf) ?? findByLabel(items, stripTableQualifier(field), labelsOf)
}

const measureLabels = (measure: string) => [measure, stripTableQualifier(measure)]

// The contract's format carries only a type, so decimals and currency symbol come from the formatter defaults.
function toFieldFormat(format: DashboardValueFormat | undefined): FieldFormat | null {
  return format?.type ? { kind: format.type } : null
}

/**
 * Server-resolved formats win; cards[].format and columns[].format fill in the measures that have none,
 * so the screen, PDF and insights all read one map.
 */
function mergeWidgetColumnFormats(widget: DashboardWidget, serverFormats: Record<string, FieldFormat> | null | undefined) {
  const merged = { ...(serverFormats ?? {}) }
  for (const entry of [...(widget.cards ?? []), ...(widget.columns ?? [])]) {
    const format = toFieldFormat(entry.format)
    if (format && !resolveColumnFormat(merged, entry.measure)) merged[entry.measure] = format
  }
  return Object.keys(merged).length ? merged : null
}

function resolveColumnLabels(widget: DashboardWidget, columns: string[]) {
  const labels: Record<string, string> = {}
  for (const entry of widget.columns ?? []) {
    const label = entry.label?.trim()
    const column = label ? findByMeasure(columns, entry.measure, measureLabels) : null
    if (label && column) labels[column] = label
  }
  return Object.keys(labels).length ? labels : null
}

function applyCardLabels(cards: AgentBuilderKpiCard[], widget: DashboardWidget): AgentBuilderKpiCard[] {
  const configured = (widget.cards ?? []).filter((entry) => entry.label?.trim())
  if (configured.length === 0) return cards
  return cards.map((card) => {
    const entry = findByMeasure(configured, card.measureKey || card.measureLabel || card.label, (item) => measureLabels(item.measure))
    return entry?.label ? { ...card, displayLabel: entry.label.trim() } : card
  })
}

function normalizeRows(columns: string[], rows: Record<string, unknown>[]) {
  return rows.map((row) => (
    Object.fromEntries(columns.map((column) => [column, normalizeCellValue(row[column])])) as Record<string, string | number | null>
  ))
}

/** The points a chart draws from `rows` when it has no `chartData` of its own. */
export function deriveChartDataFromRows(columns: string[], rows: Record<string, unknown>[]) {
  const keys = columns.length ? columns : Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
  return deriveChartData(keys, normalizeRows(keys, rows))
}

export function mapWidgetToAgentBuilderData(widget: DashboardWidget, widgetState: WidgetHydrationState): AgentBuilderReportData {
  const columns = getColumnsFromRows(widgetState.rows, widget, widgetState)
  const rows = normalizeRows(columns, widgetState.rows)

  const visualizationType = widget.type === 'groupedBar'
    ? 'GroupedBar'
    : widget.type === 'line'
      ? 'Line'
      : widget.type === 'kpi'
        ? 'KPI'
        : widget.type === 'donut'
          ? 'Donut'
          : widget.type === 'gauge'
            ? 'Gauge'
            : widget.type === 'table' || widget.type === 'dataBarTable' || widget.type === 'pivotTable'
              ? 'Table'
              : 'Bar'

  return {
    title: widget.title,
    visualizationType,
    columns,
    dimensions: getActiveDimensions(widget, widgetState),
    comparePreviousYear: widget.comparePreviousYear,
    rows,
    chartData: widgetState.chartData?.length ? widgetState.chartData : deriveChartData(columns, rows),
    comparisonChartData: widgetState.comparisonChartData ?? [],
    kpiCards: applyCardLabels(resolveKpiCards(widget, widgetState, columns, rows), widget),
    appliedFilters: Object.fromEntries((widget.filters ?? []).map((filter) => [filter.fieldName, String(filter.value)])),
    retrievedAt: new Date().toISOString(),
    platformType: 0,
    hasPaginationMetadata: widgetState.hasPaginationMetadata ?? false,
    totalRows: widgetState.totalRows,
    page: widgetState.page,
    pageSize: widgetState.pageSize,
    sortColumn: widgetState.sortColumn ?? null,
    sortDirection: widgetState.sortDirection ?? null,
    groupByDimension: widgetState.groupByDimension ?? null,
    axisMinValue: widgetState.axisMinValue ?? null,
    axisMaxValue: widgetState.axisMaxValue ?? null,
    axisMinByMeasure: widgetState.axisMinByMeasure ?? null,
    axisMaxByMeasure: widgetState.axisMaxByMeasure ?? null,
    columnFormats: mergeWidgetColumnFormats(widget, widgetState.columnFormats),
    columnLabels: resolveColumnLabels(widget, columns),
  }
}

export type WidgetInsightData = {
  chartData: Record<string, string>[]
  kpiCards: { label: string; value: string }[]
  valueFormats: Record<string, InsightValueFormat>
}

/**
 * Chart/KPI data for insight generation, with every value formatted as the dashboard displays it
 * (ReportBuilderOptions currency symbol and decimals, fractions as %, thousands separators) and keyed by
 * measure name, so the model quotes the same figures users see instead of raw numbers like 0.792.
 */
export function buildWidgetInsightData(widget: DashboardWidget, widgetState: WidgetHydrationState): WidgetInsightData {
  const data = mapWidgetToAgentBuilderData(widget, widgetState)
  const explicitFormats = new Map<string, NonNullable<DashboardWidget['cards']>[number]['format']>()
  const explicitFormatsByDisplayName = new Map<string, NonNullable<DashboardWidget['cards']>[number]['format']>()
  for (const entry of [...(widget.cards ?? []), ...(widget.columns ?? [])]) {
    if (!entry.format) continue
    explicitFormats.set(entry.measure, entry.format)
    const displayName = formatFieldDisplayName(entry.measure)
    if (!explicitFormatsByDisplayName.has(displayName)) explicitFormatsByDisplayName.set(displayName, entry.format)
  }
  // Configured formats are keyed by the raw measure ("'Table'[Margin]"); fall back to the
  // display name for keys that only carry display text (e.g. backend-provided KPI cards).
  const explicitFormatFor = (measure: string) =>
    explicitFormats.get(measure) ?? explicitFormatsByDisplayName.get(formatFieldDisplayName(measure))

  const valueFormats: Record<string, InsightValueFormat> = {}
  const formatFor = (measure: string) => {
    const resolved = valueFormats[measure]
      ?? resolveInsightValueFormat(measure, explicitFormatFor(measure)?.type, resolveColumnFormat(data.columnFormats, measure))
    valueFormats[measure] = resolved
    return resolved
  }

  // A stored numeric format keeps its decimals and currency symbol; otherwise the insight kind decides.
  const formatValue = (value: string | number, measure: string) => {
    const kind = formatFor(measure)
    const stored = resolveColumnFormat(data.columnFormats, measure)
    return stored && isNumericFormatKind(stored.kind)
      ? formatColumnValue(value, measure, data.columnFormats)
      : formatInsightValue(value, kind)
  }

  const chartData = data.chartData.map((point) => {
    const series = point.series?.length ? point.series : [{ name: 'value', value: point.value }]
    return {
      label: point.label,
      ...Object.fromEntries(series.map((entry) => [
        formatFieldDisplayName(entry.name),
        formatValue(entry.value, entry.name),
      ])),
    }
  })

  const kpiCards = data.kpiCards.map((card) => {
    const measure = card.measureKey || card.measureLabel || card.label
    const caption = card.displayLabel ?? card.measureLabel
    const appendCaption = caption && card.label && card.label !== card.measureLabel && card.label !== caption
    return {
      label: appendCaption ? `${card.label} (${caption})` : (card.displayLabel ?? card.label),
      value: formatValue(card.value, measure) + (card.unit ? ` ${card.unit}` : ''),
    }
  })

  return {
    chartData,
    kpiCards,
    valueFormats: Object.fromEntries(Object.entries(valueFormats).map(([measure, format]) => [formatFieldDisplayName(measure), format])),
  }
}
